import React, { useMemo, useState } from "react";

import { Button, IconButton, Popover, Stack, TextField, Typography } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import { CubicData, CubicPayload } from "store/types";
import { useReportsActions, useReportsMetaSelector, useReportsQueriesSelector } from "store";
import { SelectAutocomplete } from "components/forms";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DEAL_INDEX, indexNames } from "constants/reports";
import { useFormatNumericHook } from "hooks";
import { formatCubicValue } from "utils";

interface IDashboardKpiCardProps {
  item: CubicData;
  name: string;
  isLoading: boolean;
}

export const DashboardKpiCard: React.FC<IDashboardKpiCardProps> = ({ item, name, isLoading }) => {
  const { title, value, tooltip, filter, indexName } = item;

  const { KPIFields } = useReportsMetaSelector();
  const { setQueriesAction } = useReportsActions();
  const queries = useReportsQueriesSelector();
  const formatNumeric = useFormatNumericHook();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const getInitialCubicState = (): CubicPayload => ({
    title,
    filter,
    ...(filter === "PME" && { indexName: indexName || { index: DEAL_INDEX, name: "Use deal specific index" } })
  });

  const [cubic, setCubic] = useState<CubicPayload>(getInitialCubicState());

  const kpiOptionsForSelect = useMemo(() => [...KPIFields], [KPIFields]);

  const handleApply = () => {
    setAnchorEl(null);
    const queryData = {
      title: cubic.title,
      filter: cubic.filter,
      ...(cubic.filter === "PME" && cubic.indexName?.index !== DEAL_INDEX && { indexName: cubic.indexName })
    };

    setQueriesAction({
      cubicDashboard: {
        ...queries.cubicDashboard,
        [name]: queryData
      }
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCubic(getInitialCubicState());
  };

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setCubic(getInitialCubicState());
  };

  return (
    <Stack
      sx={{
        flex: "1 0 0",
        borderRadius: "8px",
        boxShadow: "0px 1px 2px rgba(18, 18, 23, 0.1)",
        backgroundColor: "#FFFFFF",
        p: "16px",
        gap: "10px",
        overflow: "hidden",
        position: "relative",
        "&:hover .kpi-settings-button": {
          opacity: 1
        }
      }}
    >
      {!isLoading && (
        <IconButton
          onClick={handleOpen}
          className="kpi-settings-button"
          sx={{
            position: "absolute",
            right: "6px",
            top: "8px",
            zIndex: 100,
            transition: "opacity 0.15s linear",
            opacity: anchorEl ? 1 : 0
          }}
        >
          <TuneIcon sx={{ height: 20, width: 20, color: "#5d7782" }} />
        </IconButton>
      )}
      {anchorEl && (
        <Popover
          open={!!anchorEl}
          onClose={handleClose}
          anchorEl={anchorEl}
          slotProps={{
            paper: {
              sx: {
                borderRadius: "8px",
                boxShadow: "sm"
              }
            }
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Stack sx={{ p: "10px", gap: "10px", borderRadius: "8px", boxShadow: "sm" }}>
            <SelectAutocomplete<string>
              options={kpiOptionsForSelect}
              defaultValue={kpiOptionsForSelect.includes(filter) ? filter : kpiOptionsForSelect[0]}
              onChange={(value) => {
                if (!value) return;
                setCubic({
                  filter: (value as string) || "",
                  ...(value !== filter ? { title: value as string } : { title }),
                  ...(value === "PME" && { indexName: { index: DEAL_INDEX, name: "Use deal specific index" } })
                });
              }}
              disableClearable
              renderInput={(params) => <TextField {...params} label="Value" size="small" />}
              limitTags={2}
            />
            {cubic.filter === "PME" && (
              <SelectAutocomplete<string>
                options={["Use deal specific index", ...Object.values(indexNames)]}
                defaultValue={cubic.indexName?.name}
                disableClearable
                onChange={(selectedValue) => {
                  if (!selectedValue) return;
                  if (selectedValue === "Use deal specific index") {
                    setCubic({ ...cubic, indexName: { index: DEAL_INDEX, name: selectedValue as string } });
                    return;
                  }
                  const indexKey = Object.entries(indexNames).find(([, val]) => val === selectedValue)?.[0];
                  if (indexKey) {
                    setCubic({ ...cubic, indexName: { index: indexKey, name: selectedValue as string } });
                  }
                }}
                renderInput={(params) => <TextField {...params} label="Index" size="small" />}
                limitTags={2}
              />
            )}
            <TextField
              value={cubic.title}
              label="Rename"
              variant="outlined"
              onChange={(event) => {
                setCubic({
                  ...cubic,
                  title: event.target.value
                });
              }}
            />
            <Button variant="contained" sx={{ alignSelf: "flex-end" }} onClick={handleApply}>
              Apply
            </Button>
          </Stack>
        </Popover>
      )}
      <Typography
        sx={{
          fontSize: "14px",
          lineHeight: "16px",
          fontWeight: 400,
          color: "#496682",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}
        variant="body3"
      >
        {title}
      </Typography>
      <MaterialTooltip isShow variant="dark" title={tooltip || value} placement="bottom">
        <Typography
          sx={{
            fontSize: "24px",
            lineHeight: "32px",
            fontWeight: 700,
            color: "#071F35",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        >
          {formatCubicValue(item, formatNumeric)}
        </Typography>
      </MaterialTooltip>
    </Stack>
  );
};
